import { useEffect, useState, useCallback } from 'react'
import { CreditCard, Plus, X, Trash2, Calculator } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

const money = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
const ESCOPO_LABEL = { global: 'Global', licenca: 'Licença', produto: 'Produto', categoria: 'Categoria', tipo_licenca: 'Tipo de Licença', campanha: 'Campanha' }

export default function Parcelamento() {
  const [regras, setRegras] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [simValor, setSimValor] = useState(1000)
  const [sim, setSim] = useState(null)

  const carregar = useCallback(() => {
    setLoading(true)
    api.get('/parcelamento').then((r) => setRegras(r.data.data || [])).catch(() => setRegras([])).finally(() => setLoading(false))
  }, [])
  useEffect(() => { carregar() }, [carregar])

  const simular = () => api.get(`/parcelamento/simular?valor=${Number(simValor) || 0}`).then((r) => setSim(r.data)).catch(() => errorModal('Erro ao simular.'))
  const del = async (id) => { if (await confirmModal('Excluir esta regra?')) api.delete(`/parcelamento/${id}`).then(carregar).catch(() => errorModal('Erro.')) }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow"><CreditCard size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Parcelamento</h1>
            <p className="text-sm text-gray-500">Regras de parcelamento de licenças e produtos (com ou sem juros), por escopo.</p>
          </div>
        </div>
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16} /> Nova Regra</button>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1"><Calculator size={14} /> Simulador</p>
        <div className="flex items-end gap-2 flex-wrap">
          <div><label className="text-xs text-gray-500">Valor (R$)</label><input type="number" value={simValor} onChange={(e) => setSimValor(e.target.value)} className="block border border-gray-200 rounded-xl px-3 py-2 text-sm w-40" /></div>
          <button onClick={simular} className="bg-accent-500 text-white px-4 py-2 rounded-xl text-sm font-semibold">Simular</button>
        </div>
        {sim && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {sim.opcoes.map((o) => (
              <div key={o.parcelas} className={`rounded-xl px-3 py-2 text-sm border ${o.tem_juros ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <p className="font-bold text-gray-900">{o.parcelas}x {money(o.valor_parcela)}</p>
                <p className="text-[11px] text-gray-500">total {money(o.total)} · {o.tem_juros ? 'c/ juros' : 'sem juros'}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader text="Carregando..." /></div> : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase"><tr><th className="px-4 py-2">Nome</th><th className="px-4 py-2">Escopo</th><th className="px-4 py-2 text-right">Máx parcelas</th><th className="px-4 py-2 text-right">Sem juros</th><th className="px-4 py-2 text-right">Juros % a.m.</th><th className="px-4 py-2"></th></tr></thead>
            <tbody>
              {regras.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-medium text-gray-900">{r.nome || '—'}</td>
                  <td className="px-4 py-2">{ESCOPO_LABEL[r.escopo] || r.escopo}{r.alvo_categoria ? ` · ${r.alvo_categoria}` : r.alvo_id ? ` · #${r.alvo_id}` : ''}</td>
                  <td className="px-4 py-2 text-right">{r.max_parcelas}x</td>
                  <td className="px-4 py-2 text-right">{r.parcelas_sem_juros}x</td>
                  <td className="px-4 py-2 text-right">{r.taxa_juros_mensal}%</td>
                  <td className="px-4 py-2 text-right"><button onClick={() => del(r.id)}><Trash2 size={14} className="text-red-400 hover:text-red-600" /></button></td>
                </tr>
              ))}
              {regras.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Nenhuma regra — usa o parcelamento padrão dos Parâmetros do Plano.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {open && <RegraForm onClose={() => setOpen(false)} onSalvo={() => { setOpen(false); carregar() }} />}
    </div>
  )
}

function RegraForm({ onClose, onSalvo }) {
  const [f, setF] = useState({ nome: '', escopo: 'global', alvo_id: '', alvo_categoria: '', max_parcelas: 12, parcelas_sem_juros: 3, taxa_juros_mensal: 2.99, ativo: true })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const salvar = () => {
    setSaving(true)
    api.post('/parcelamento', {
      nome: f.nome || null, escopo: f.escopo, alvo_id: f.alvo_id ? Number(f.alvo_id) : null,
      alvo_categoria: f.alvo_categoria || null, max_parcelas: Number(f.max_parcelas) || 1,
      parcelas_sem_juros: Number(f.parcelas_sem_juros) || 0, taxa_juros_mensal: Number(f.taxa_juros_mensal) || 0, ativo: true,
    }).then(() => { successModal('Regra salva.'); onSalvo() }).catch(() => errorModal('Erro.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">Nova Regra de Parcelamento</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <input value={f.nome} onChange={set('nome')} placeholder="Nome (ex.: Produtos até 3x sem juros)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <select value={f.escopo} onChange={set('escopo')} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm">
          {Object.entries(ESCOPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {f.escopo === 'categoria'
          ? <input value={f.alvo_categoria} onChange={set('alvo_categoria')} placeholder="Categoria alvo (ex.: energia)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          : f.escopo !== 'global' && <input type="number" value={f.alvo_id} onChange={set('alvo_id')} placeholder="ID do alvo (produto/campanha/faixa)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />}
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[11px] text-gray-500">Máx parcelas<input type="number" value={f.max_parcelas} onChange={set('max_parcelas')} className="w-full border border-gray-200 rounded-xl px-2 py-2 text-sm" /></label>
          <label className="text-[11px] text-gray-500">Sem juros<input type="number" value={f.parcelas_sem_juros} onChange={set('parcelas_sem_juros')} className="w-full border border-gray-200 rounded-xl px-2 py-2 text-sm" /></label>
          <label className="text-[11px] text-gray-500">Juros % a.m.<input type="number" value={f.taxa_juros_mensal} onChange={set('taxa_juros_mensal')} className="w-full border border-gray-200 rounded-xl px-2 py-2 text-sm" /></label>
        </div>
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar Regra'}</button>
      </div>
    </div>
  )
}
